import { useState, type FormEvent } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useSignUp } from "@clerk/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AuthContent,
  AuthErrorAlert,
  AuthShell,
  ClerkCaptcha,
  CodeInput,
  FieldMessage,
  OAuthButtons,
  OrDivider,
  TextButton,
} from "@/components/auth/AuthLayout";
import {
  APP_HOME,
  OAUTH_STALL_MS,
  PRIVACY_URL,
  SSO_CALLBACK_PATH,
  TERMS_URL,
  navigateToApp,
  useResendCooldown,
  type AuthOAuthStrategy,
} from "@/components/auth/auth-utils";

type Step = "form" | "code" | "invite" | "continue";

// Missing fields the "finish signing up" step can collect. Anything else
// sends the user back to start over.
const COLLECTABLE_FIELDS = new Set(["first_name", "last_name", "legal_accepted"]);

const HEADLINE =
  "Amy finds the creators, writes the emails and negotiates the deal. You approve it.";

const LEGAL_REQUIRED = "Accept the Terms of Use and Privacy Policy to continue.";

/**
 * Sign up with an email code or Google / Microsoft, on Clerk's signal-based
 * useSignUp. Also handles the two hand-offs Clerk's prebuilt page did:
 * - an organization invitation (?__clerk_ticket=…&__clerk_status=sign_up),
 *   which needs no email verification;
 * - an OAuth sign-up that came back from /sso-callback still missing
 *   something (?continue — e.g. the legal consent production requires).
 */
export default function SignUpPage() {
  const { signUp, errors, fetchStatus } = useSignUp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticket = searchParams.get("__clerk_ticket");
  const ticketStatus = searchParams.get("__clerk_status");

  const [step, setStep] = useState<Step>(() => {
    if (ticket) return "invite";
    if (searchParams.has("continue") && signUp.status === "missing_requirements") {
      return "continue";
    }
    return "form";
  });
  const [firstName, setFirstName] = useState(signUp.firstName ?? "");
  const [lastName, setLastName] = useState(signUp.lastName ?? "");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalError, setLegalError] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [oauthPending, setOauthPending] = useState<AuthOAuthStrategy | null>(null);
  const cooldown = useResendCooldown();
  const busy = fetchStatus === "fetching";

  // An invitation for someone who already has an account signs in instead.
  if (ticket && ticketStatus === "sign_in") {
    return <Navigate to={`/login?${searchParams.toString()}`} replace />;
  }
  if (ticket && ticketStatus === "complete") {
    return (
      <AuthShell headline={HEADLINE}>
        <AuthContent
          title="Invitation already accepted"
          description="You've already joined. Sign in to continue."
        >
          <Button asChild className="h-10 text-sm">
            <Link to="/login">Go to sign in</Link>
          </Button>
        </AuthContent>
      </AuthShell>
    );
  }

  const requireLegal = () => {
    if (legalAccepted) return true;
    setLegalError(LEGAL_REQUIRED);
    return false;
  };

  const names = () => ({
    firstName: firstName.trim() || undefined,
    lastName: lastName.trim() || undefined,
  });

  /** Finalize a complete sign-up, or route to whatever it still needs. */
  const advance = async () => {
    if (signUp.status === "complete") {
      await signUp.finalize({ navigate: navigateToApp(navigate) });
      return;
    }
    if (signUp.unverifiedFields.includes("email_address")) {
      const { error } = await signUp.verifications.sendEmailCode();
      if (error) return;
      setCode("");
      setStep("code");
      cooldown.start();
      return;
    }
    setStep("continue");
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setStepError(null);
    if (!requireLegal()) return;
    const { error } = await signUp.create({
      emailAddress: email.trim(),
      ...names(),
      legalAccepted: true,
    });
    if (error) return;
    await advance();
  };

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    setStepError(null);
    if (!ticket || !requireLegal()) return;
    const { error } = await signUp.ticket({ ticket, ...names(), legalAccepted: true });
    if (error) return;
    await advance();
  };

  const handleContinue = async (e: FormEvent) => {
    e.preventDefault();
    setStepError(null);
    const missing = signUp.missingFields;
    if (missing.includes("legal_accepted") && !requireLegal()) return;
    const { error } = await signUp.update({
      ...names(),
      ...(missing.includes("legal_accepted") ? { legalAccepted: true } : {}),
    });
    if (error) return;
    if (signUp.status !== "complete" && !signUp.unverifiedFields.includes("email_address")) {
      setStepError("We couldn't finish your sign-up. Start over and try again.");
      return;
    }
    await advance();
  };

  const verify = async (value: string) => {
    setStepError(null);
    const { error } = await signUp.verifications.verifyEmailCode({ code: value });
    if (error) return;
    await advance();
  };

  const handleResend = async () => {
    const { error } = await signUp.verifications.sendEmailCode();
    if (error) return;
    cooldown.start();
    toast.success(`New code sent to ${signUp.emailAddress ?? email.trim()}`);
  };

  const handleOAuth = async (strategy: AuthOAuthStrategy) => {
    setStepError(null);
    if (!requireLegal()) return;
    setOauthPending(strategy);
    const { error } = await signUp.sso({
      strategy,
      redirectUrl: APP_HOME,
      redirectCallbackUrl: SSO_CALLBACK_PATH,
      legalAccepted: true,
    });
    if (error) {
      setOauthPending(null);
      return;
    }
    // On success the browser is on its way to the provider. If it still
    // hasn't left after a few seconds, let the button be pressed again.
    window.setTimeout(() => setOauthPending(null), OAUTH_STALL_MS);
  };

  const startOver = () => {
    void signUp.reset();
    setStep("form");
    setStepError(null);
    navigate("/signup", { replace: true });
  };

  // Production sign-up is invite-only; say so instead of Clerk's generic text.
  const restricted = errors.global?.some((err) => err.code?.includes("restricted"));
  const globalMessage = restricted
    ? "Uplodio is invite-only. Ask your team's admin to invite you, then use the link in that email."
    : stepError;

  const footer = (
    <>
      Already have an account?{" "}
      <Link to="/login" className="font-medium text-primary hover:underline">
        Sign in
      </Link>
    </>
  );

  const nameFields = (
    <div className="grid grid-cols-2 gap-3">
      <div className="flex flex-col gap-2">
        <Label htmlFor="sign_up_first_name">First name</Label>
        <Input
          id="sign_up_first_name"
          className="h-10"
          autoComplete="given-name"
          placeholder="Optional"
          value={firstName}
          disabled={busy}
          aria-invalid={!!errors.fields.firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <FieldMessage error={errors.fields.firstName} />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="sign_up_last_name">Last name</Label>
        <Input
          id="sign_up_last_name"
          className="h-10"
          autoComplete="family-name"
          placeholder="Optional"
          value={lastName}
          disabled={busy}
          aria-invalid={!!errors.fields.lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <FieldMessage error={errors.fields.lastName} />
      </div>
    </div>
  );

  const legalField = (
    <LegalConsent
      checked={legalAccepted}
      disabled={busy}
      error={legalError ?? errors.fields.legalAccepted?.message ?? null}
      onCheckedChange={(checked) => {
        setLegalAccepted(checked);
        if (checked) setLegalError(null);
      }}
    />
  );

  const submitButton = (label: string, disabled = false) => (
    <Button
      type="submit"
      className="h-10 text-sm"
      disabled={busy || disabled || oauthPending !== null}
    >
      {busy && <Spinner />}
      {label}
    </Button>
  );

  if (step === "code") {
    return (
      <AuthShell headline={HEADLINE}>
        <AuthContent
          title="Verify your email"
          description={
            <>
              Enter the 6-digit code we sent to{" "}
              <span className="font-medium text-foreground">
                {signUp.emailAddress ?? email.trim()}
              </span>
            </>
          }
          footer={footer}
        >
          <AuthErrorAlert errors={errors.global} message={globalMessage} />
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void verify(code);
            }}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="sign_up_code" className="sr-only">
                Verification code
              </Label>
              <CodeInput
                id="sign_up_code"
                autoFocus
                value={code}
                disabled={busy}
                invalid={!!errors.fields.code}
                onChange={setCode}
                onComplete={(value) => void verify(value)}
              />
              <FieldMessage error={errors.fields.code} />
            </div>
            {submitButton("Continue", code.length < 6)}
            <div className="flex items-center justify-between">
              <TextButton onClick={startOver} disabled={busy}>
                Use a different email
              </TextButton>
              <TextButton
                onClick={() => void handleResend()}
                disabled={busy || cooldown.left > 0}
              >
                {cooldown.left > 0 ? `Resend code in ${cooldown.left}s` : "Resend code"}
              </TextButton>
            </div>
          </form>
        </AuthContent>
      </AuthShell>
    );
  }

  if (step === "invite") {
    return (
      <AuthShell headline={HEADLINE}>
        <AuthContent
          title="Join your team on Uplodio"
          description="You've been invited. Add your name to finish creating your account."
          footer={footer}
        >
          <AuthErrorAlert errors={errors.global} message={globalMessage} />
          <form className="flex flex-col gap-4" onSubmit={(e) => void handleInvite(e)}>
            {nameFields}
            {legalField}
            <ClerkCaptcha />
            {submitButton("Accept invitation")}
          </form>
        </AuthContent>
      </AuthShell>
    );
  }

  if (step === "continue") {
    const missing = signUp.missingFields;
    const collectable = missing.every((field) => COLLECTABLE_FIELDS.has(field));
    return (
      <AuthShell headline={HEADLINE}>
        <AuthContent
          title="Finish signing up"
          description={
            signUp.emailAddress
              ? `Almost done, ${signUp.emailAddress}.`
              : "Just one more step."
          }
          footer={footer}
        >
          <AuthErrorAlert errors={errors.global} message={globalMessage} />
          {collectable ? (
            <form className="flex flex-col gap-4" onSubmit={(e) => void handleContinue(e)}>
              {(missing.includes("first_name") || missing.includes("last_name")) && nameFields}
              {missing.includes("legal_accepted") && legalField}
              <ClerkCaptcha />
              {submitButton("Continue")}
            </form>
          ) : (
            <p className="text-center text-sm text-muted-foreground">
              We need details this page can't collect.
            </p>
          )}
          <TextButton onClick={startOver} className="self-center">
            Start over
          </TextButton>
        </AuthContent>
      </AuthShell>
    );
  }

  return (
    <AuthShell headline={HEADLINE}>
      <AuthContent
        title="Create your account"
        description="Use your work email. If your team invited you, use the address the invitation went to."
        footer={footer}
      >
        <AuthErrorAlert errors={errors.global} message={globalMessage} />
        <OAuthButtons pending={oauthPending} disabled={busy} onSelect={(s) => void handleOAuth(s)} />
        <OrDivider />
        <form className="flex flex-col gap-4" onSubmit={(e) => void handleCreate(e)}>
          {nameFields}
          <div className="flex flex-col gap-2">
            <Label htmlFor="sign_up_email">Email address</Label>
            <Input
              id="sign_up_email"
              className="h-10"
              type="email"
              autoComplete="email"
              required
              placeholder="name@company.com"
              value={email}
              disabled={busy}
              aria-invalid={!!errors.fields.emailAddress}
              onChange={(e) => setEmail(e.target.value)}
            />
            <FieldMessage error={errors.fields.emailAddress} />
          </div>
          {legalField}
          <ClerkCaptcha />
          <FieldMessage error={errors.fields.captcha} />
          {submitButton("Continue", !email.trim())}
        </form>
      </AuthContent>
    </AuthShell>
  );
}

function LegalConsent({
  checked,
  disabled,
  error,
  onCheckedChange,
}: {
  checked: boolean;
  disabled?: boolean;
  error: string | null;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-2">
        <Checkbox
          id="sign_up_legal"
          className="mt-0.5"
          checked={checked}
          disabled={disabled}
          aria-invalid={!!error}
          onCheckedChange={(value) => onCheckedChange(value === true)}
        />
        <Label
          htmlFor="sign_up_legal"
          className="text-xs font-normal leading-snug text-muted-foreground"
        >
          <span>
            I agree to the{" "}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-foreground underline underline-offset-2"
            >
              Terms of Use
            </a>{" "}
            and{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-foreground underline underline-offset-2"
            >
              Privacy Policy
            </a>
          </span>
        </Label>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
